import React, { FC, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ProjectId, SpaceTabId } from 'api/models';
import { SpaceTabAPI, SpaceTabDocumentType } from 'api/space/space-tab';
import { Tabs as TabsAtom } from 'components/atoms/tabs';
import { toastService } from 'components/molecules/toast';
import { addSpaceTab, SpaceTab as SpaceTabType } from 'data/space-tabs';
import {
  createSpaceTabSagaAction,
  createSpaceTabSuccessSagaAction,
} from 'data/space-tabs/actions';
import { getIsCreating } from 'data/space-tabs/selectors';
import { Template } from 'data/templates/api';
import { useProjectSpacePermission } from 'hooks/permissions';
import { useSpaceTabHistoryLocalStorageState } from 'hooks/use-space-tab-local-storage-state';
import { useTranslation } from 'hooks/use-translation';
import { getSpaceTabDetailUrl } from 'utils/urls';
import { useCurrentSpace } from './context';
import { SpaceNewTabPaneContent, SpaceTabContainer, Tab } from './space-tab';
import { NewTabPane, Tabs } from './styles';

const DEFAULT_TAB_TITLE = 'Untitled';

const { TabPane } = TabsAtom;

interface SpacesProps {
  data: SpaceTabType[];
  activeTabKey: string;
}

export const Spaces: FC<SpacesProps> = (props) => {
  const { data, activeTabKey } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { spaceId, project, spaceName } = useCurrentSpace();
  const { projectId } = project;
  const isCreating = useSelector(getIsCreating);

  const handleAdd = useCallback(() => {
    dispatch(addSpaceTab({ space: { spaceId }, url: '', spaceTabId: 'new' }));
  }, [spaceId, dispatch]);

  const spaceTabHistory = useSpaceTabHistoryLocalStorageState();

  const handleTabsOnChange = useCallback(
    (selectedKey: string) => {
      history.push(
        getSpaceTabDetailUrl(SpaceTabId(projectId, spaceId, selectedKey)),
      );
      spaceTabHistory.setValue(spaceId, selectedKey);
    },
    [history, projectId, spaceId, spaceTabHistory],
  );

  const handleOnEdit = (_, action: 'add' | 'remove') => {
    if (action === 'add') {
      handleTabsOnChange('new');
    }
  };

  useEffect(() => {
    if (activeTabKey === 'new') {
      handleAdd();
    }
  }, [activeTabKey, handleAdd]);

  const mutationFn = (payload) => SpaceTabAPI.createSpaceTab(payload);
  const { mutate, isLoading } = useMutation(mutationFn);
  const errorToastTitle = useTranslation('space_tab.embed.error.title');
  const errorToastMessage = useTranslation('space_tab.embed.error.message');
  const { error } = toastService({
    title: errorToastTitle,
    message: errorToastMessage,
  });

  const handleNewEmbed = (values, form) => {
    const { url } = values;

    mutate(
      { url, space: { spaceId } },
      {
        onSuccess: (response) => {
          queryClient
            .refetchQueries(['space', response?.space?.spaceId])
            .then(() => {
              dispatch(createSpaceTabSuccessSagaAction(response));
            });

          form.resetFields();
        },
        onError: () => {
          error();
        },
      },
    );
  };

  const handleNewDocument = () => {
    dispatch(
      createSpaceTabSagaAction({
        spaceTabType: SpaceTabDocumentType.RocketlaneDocument,
        spaceTabTitle: DEFAULT_TAB_TITLE,
        space: { spaceId },
        spaceTabId: 'new',
        handleSuccess: (redirectPath: string, selectedSpaceId: number) => {
          queryClient.refetchQueries(['space', selectedSpaceId]).then(() => {
            history.push(redirectPath);
          });
        },
      }),
    );
  };

  const { canCreate } = useProjectSpacePermission(ProjectId(projectId));

  const templateErrorToastTitle = useTranslation(
    'space_tab.template.error.title',
  );
  const templateErrorToastMessage = useTranslation(
    'space_tab.template.error.message',
  );
  const { error: templateError } = toastService({
    title: templateErrorToastTitle,
    message: templateErrorToastMessage,
  });

  const creatFromTemplateMutation = useMutation(mutationFn);
  const handleOnChooseTemplate = ({ templateId }: Template) => {
    creatFromTemplateMutation.mutate(
      {
        space: { spaceId },
        entitySource: {
          sourceId: templateId,
        },
      },
      {
        onSuccess: (response) => {
          dispatch(createSpaceTabSuccessSagaAction(response));
        },
        onError: () => {
          templateError();
        },
      },
    );
  };

  return (
    <Tabs
      name={spaceName}
      hideAdd={!canCreate}
      activeKey={activeTabKey}
      onChange={handleTabsOnChange}
      onEdit={handleOnEdit}
    >
      {data.map((spaceTab) =>
        spaceTab.spaceTabId > 0 ? (
          <TabPane tab={<Tab data={spaceTab} />} key={spaceTab.spaceTabId}>
            <SpaceTabContainer
              spaceTabId={SpaceTabId(projectId, spaceId, spaceTab.spaceTabId)}
            />
          </TabPane>
        ) : (
          <NewTabPane
            tab={<FormattedMessage id="space_tab.new_tab" />}
            key="new"
          >
            <SpaceNewTabPaneContent
              isCreateInProgress={isCreating}
              isCreateFromTemplateInProgress={
                creatFromTemplateMutation.isLoading
              }
              isEmbedInProgress={isLoading}
              onCreateDocument={handleNewDocument}
              onCreateEmbed={handleNewEmbed}
              onChooseTemplate={handleOnChooseTemplate}
            />
          </NewTabPane>
        ),
      )}
    </Tabs>
  );
};
